import List from '@pelckmans/elementis/components/list';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CurrentGroup, Member } from '../../api/groups';
import { AssignmentActionArea } from './AssignmentActionArea';
import type { GroupAudience, MemberAudience } from './sharedMaterialApi';
import { useShareModalContext } from './useShareModalContext';

const BASE = 'PAGES.TOC_LEVEL.MODALS.SHARE';

interface ShareModalGroupItemProps {
  group: CurrentGroup;
  members: Member[];
  selectedGroup?: GroupAudience;
  selectedMembers: MemberAudience[];
  containsNonScorable: boolean;
  containsNonShareableAsAssignment: boolean;
  searchValue?: string;
}

export const ShareModalGroupItem = ({
  group,
  members,
  selectedGroup: isSelectedGroup,
  selectedMembers,
  containsNonScorable,
  containsNonShareableAsAssignment,
  searchValue,
}: ShareModalGroupItemProps) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const { handleGroupToggle, handleMemberToggle } = useShareModalContext();
  const handleCollapseToggle = () => setCollapsed(c => !c);

  useEffect(() => {
    if (!searchValue) return;

    const hasMatchingMember = members.some(member =>
      `${member.lastName} ${member.firstName}`.toLowerCase().includes(searchValue.toLowerCase()),
    );
    setCollapsed(!hasMatchingMember);
  }, [members, searchValue]);

  const hasMemberAssignments = selectedMembers.some(m => m.assignment);
  const hasMemberRepeatableAssignments = selectedMembers.some(m => m.assignment?.repeatable);

  const groupProps = {
    as: 'checkbox' as const,
    level: 1 as const,
    title: group.name,
    checked: !!isSelectedGroup,
    indeterminate: selectedMembers.length > 0,
    onClick: () => handleGroupToggle(group),
    actionArea: !containsNonShareableAsAssignment && (
      <AssignmentActionArea
        disabled={!isSelectedGroup}
        audience={isSelectedGroup}
        containsNonScorable={containsNonScorable}
        indeterminate={hasMemberAssignments}
        indeterminateRepeatable={hasMemberRepeatableAssignments}
      />
    ),
  };

  return (
    <>
      {members.length > 0 ? (
        <List.Item
          {...groupProps}
          key={group.id}
          collapsed={collapsed}
          collapseButtonLabelText={t(`${BASE}.ACTIONS.GROUP_COLLAPSE_BUTTON_LABEL`)}
          onToggleCollapse={handleCollapseToggle}
        />
      ) : (
        <List.Item key={group.id} {...groupProps} />
      )}
      {!collapsed &&
        members?.map(member => {
          const isSelectedMember = selectedMembers.find(m => m.userId === member.id);
          return (
            <List.Item
              key={member.id}
              as="checkbox"
              level={2}
              title={`${member.lastName} ${member.firstName}`}
              disabled={!!isSelectedGroup}
              checked={!!isSelectedGroup || !!isSelectedMember}
              onClick={() => handleMemberToggle(group, member.id)}
              actionArea={
                !containsNonShareableAsAssignment && (
                  <AssignmentActionArea
                    disabled={!!isSelectedGroup || !isSelectedMember}
                    audience={isSelectedMember}
                    containsNonScorable={containsNonScorable}
                    parentAudience={isSelectedGroup}
                  />
                )
              }
            />
          );
        })}
    </>
  );
};
